import { FC, useRef } from "react";

import { faMapMarkerAlt, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Image from "common/blocks/Image";
import useWhoAmIContext from "common/providers/whoAmI/useWhoAmIContext";
import SmartLink from "common/wrapper/SmartLink";
import { motion } from "framer-motion";
import useHasMounted from "hooks/useHasMounted/useHasMounted";
import type { ResolvedMegaMenuPromo } from "resolvers/base/megaMenuSectionPromoResolver";
import type { ResolvedNavItem } from "resolvers/base/navigationResolver";
import { ResolvedTertiaryLink } from "resolvers/data/header/menuResolver";
import "swiper/css";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import getPathByType, { UrlPathTypes } from "utils/getPathByType";

import { StepView } from "../interfaces";
import styles from "./MainMenu.module.scss";
import { FloatingLink, MainMenuContainer } from "./MainMenuContainer";
import { MainMenuList } from "./MainMenuList";

const cx = classNames.bind(styles);

const variants = {
    open: { opacity: 1, x: 0, z: 0, y: 0 },
    closed: { opacity: 0, x: 0, z: 0, y: 0 },
};

export type MainMenuProps = {
    menuHeadings: {
        primary: string;
        secondary: string;
        tertiary: string;
    };
    navItems: {
        primary: ResolvedNavItem[];
        secondary: ResolvedNavItem[];
        tertiary: ResolvedTertiaryLink[];
    };
    handleNextStepView: (stepView: StepView) => void;
    mobilePromos: ResolvedMegaMenuPromo[];
    prefetch?: boolean;
    showMobilePromos: boolean;
    showSellButton: boolean;
    phoneLinesText: string;
    isPhoneLinesOpen: boolean;
};

export const MainMenu: FC<MainMenuProps> = ({
    menuHeadings,
    navItems,
    handleNextStepView,
    mobilePromos,
    prefetch = false,
    showMobilePromos = false,
    showSellButton = false,
    phoneLinesText,
    isPhoneLinesOpen,
}) => {
    const hasMounted = useHasMounted();
    const footerRef = useRef<HTMLDivElement>(null);
    const { primary, secondary, tertiary } = navItems;
    const { isLoggedIn } = useWhoAmIContext();

    const currentURL =
        hasMounted && typeof window !== "undefined"
            ? `?currentUrl=${window.location.pathname}`
            : "";

    const signOutLink: FloatingLink = {
        path: `/mymotorpoint/logout${currentURL}`,
        target: "_self",
        text: "Sign out",
        showOnlyWhenLoggedIn: true,
        onClick: () => {
            // setListId(newListId);
        },
    };

    const {
        primary: primaryMenuHeading,
        secondary: secondaryMenuHeading,
        tertiary: tertiaryMenuHeading,
    } = menuHeadings;

    const swiperOpts = {
        slidesPerView: 1.3,
        slidesPerGroupAuto: true,
        className: cx("swiper"),
        modules: [A11y, Navigation],
        simulateTouch: false,
        spaceBetween: 10,
    };

    const myMotorpoint = !isLoggedIn
        ? [
              {
                  path: getPathByType({ type: UrlPathTypes.MyMotorpointLoginPage }),
                  target: "_self",
                  linkText: "Sign In",
              },
              {
                  path: getPathByType({ type: UrlPathTypes.MyMotorpointPage }),
                  target: "_self",
                  linkText: "Create account",
              },
          ]
        : [
              {
                  path: "/mymotorpoint/my-activity",
                  target: "_self",
                  linkText: "My Activity",
              },
              {
                  path: "/mymotorpoint/my-payments",
                  target: "_self",
                  linkText: "My Payments",
              },
              {
                  path: "/shortlist",
                  target: "_self",
                  linkText: "My Shortlist",
              },
              {
                  path: "/mymotorpoint/my-details",
                  target: "_self",
                  linkText: "Personal Details",
              },
              {
                  path: "/mymotorpoint/my-login",
                  target: "_self",
                  linkText: "Login Details",
              },
              {
                  path: "/mymotorpoint/my-preferences",
                  target: "_self",
                  linkText: "Communication Preferences",
              },
          ];

    return (
        <motion.div
            initial="closed"
            animate={["open"]}
            variants={variants}
            transition={{ duration: 0.2 }}
        >
            <div
                className={cx("menuScrollingContainer")}
                style={{
                    overflowY: "auto",
                }}
            >
                {showMobilePromos && mobilePromos.length && (
                    <div className={cx("swipeContainer")}>
                        <Swiper {...swiperOpts}>
                            {mobilePromos.map((item) => (
                                <SwiperSlide key={item.id}>
                                    <SmartLink href={item.link} prefetch={!!prefetch}>
                                        <a className={cx("swipeItem")}>
                                            <Image
                                                className={cx("swipeItemImage")}
                                                objectFit="cover"
                                                src={item.image.src}
                                                layout="fill"
                                                alt={item.image.alt || item.label}
                                            />
                                            <span className={cx("swipeItemContent")}>
                                                <span className={cx("swipeItemTitle")}>
                                                    {item.label}
                                                </span>
                                                <span className={cx("swipeItemDescription")}>
                                                    {item.description}
                                                </span>
                                            </span>
                                        </a>
                                    </SmartLink>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}

                {showSellButton && (
                    <div className={cx("sellButtonWrapper")}>
                        <Button
                            className={cx("sellButton")}
                            href={getPathByType({ type: UrlPathTypes.ValueMyCarPage })}
                            size="medium"
                            theme="tertiary"
                        >
                            Sell
                        </Button>
                    </div>
                )}

                {!!primary.length && (
                    <MainMenuList
                        handleNextStepView={handleNextStepView}
                        items={primary}
                        title={primaryMenuHeading}
                        priority="primary"
                    />
                )}
                {!!secondary.length && (
                    <MainMenuList
                        handleNextStepView={handleNextStepView}
                        items={secondary}
                        className={styles.menuSecondary}
                        title={secondaryMenuHeading}
                        priority="secondary"
                    />
                )}

                <MainMenuContainer
                    overrideMenuClassName="myMotorpoint"
                    items={myMotorpoint}
                    title="MyMotorpoint"
                    isLoggedIn={isLoggedIn}
                    floatingLink={signOutLink}
                />

                {!!tertiary.length && (
                    <MainMenuContainer
                        isLoggedIn={isLoggedIn}
                        items={tertiary}
                        title={tertiaryMenuHeading}
                    />
                )}
            </div>
            <div ref={footerRef} className={cx("footer")}>
                <div className={cx("footerItem")}>
                    <div className={cx("iconButton", "phone")}>
                        <FontAwesomeIcon icon={faPhone} height={12} />
                    </div>
                    <div className={cx("phoneLinesContainer")}>
                        <span className={cx("footerItemLink")}>
                            <DynamicPhoneNumber className={cx("text")} isLink prefix="Call" />
                        </span>
                        <div className={cx("openingTimesContainer")}>
                            <span className={cx("openingTimesIcon", { isPhoneLinesOpen })}></span>
                            <span className={cx("openingTimesText")}>{phoneLinesText}</span>
                        </div>
                    </div>
                </div>
                <div className={cx("footerItem", "stores")}>
                    <div className={cx("iconButton")}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} height={12} />
                    </div>
                    <span className={cx("footerItemLink")}>
                        <SmartLink
                            href={getPathByType({
                                type: UrlPathTypes.BranchesPage,
                            })}
                            prefetch={!!prefetch}
                        >
                            <a>Stores</a>
                        </SmartLink>
                    </span>
                </div>
            </div>
        </motion.div>
    );
};
